import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Typography, Tooltip, Empty } from 'antd';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import './GanttChart.css';

// 扩展dayjs插件
dayjs.extend(isSameOrBefore);

const { Text } = Typography;

/**
 * 甘特图组件
 * 用于显示任务和执行人的工作时间分布
 * @param {Array} logs - 工作日志数据
 * @param {Array} dateRange - 日期范围 [startDate, endDate]
 */
const GanttChart = ({ logs = [], dateRange = [] }) => {
  // 生成日期范围内的所有日期，确保当前日期在最右侧
  const dateColumns = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return [];
    const [start, end] = dateRange;
    const today = dayjs();
    const dates = [];
    let current = start.clone();
    
    // 生成从开始日期到结束日期的所有日期
    while (current.isSameOrBefore(end, 'day')) {
      dates.push(current.clone());
      current = current.add(1, 'day');
    }
    
    // 如果当前日期不在范围内，将其添加到最右侧
    if (!dates.some(date => date.isSame(today, 'day'))) {
      if (today.isAfter(end, 'day')) {
        dates.push(today.clone());
      } else if (today.isBefore(start, 'day')) {
        dates.unshift(today.clone());
      }
    }
    
    return dates;
  }, [dateRange]);

  /**
   * 根据所有日志构建甘特数据
   * - 任务层级：taskDailyLogs 记录某任务某天的日志列表
   * - 用户层级：dailyLogs 记录某用户在该任务下某天的日志列表
   */
  const ganttData = useMemo(() => {
    const taskMap = new Map();
    
    logs.forEach(log => {
      const task = log?.task;
      const user = log?.user;
      // 中文注释：将日志日期统一归一化到 YYYY-MM-DD，避免因时区/时间部分造成的跨日偏移
      const logDate = normalizeToLocalDate(log.logDate);
      const hours = Number(log.workHours || 0);
      
      if (!task || !user || !logDate.isValid()) return;
      
      const taskKey = task.id;
      if (!taskMap.has(taskKey)) {
        taskMap.set(taskKey, {
          taskId: task.id,
          taskTitle: task.title,
          // 新增：保存任务计划开始/结束日期（用于任务行计划区间底色）
          startDate: task.startDate || null,
          dueDate: task.dueDate || null,
          users: new Map(),
          taskDailyHours: new Map(), // 任务层每日总工时
          taskDailyLogs: new Map(),  // 任务层每日日志列表
        });
      }
      
      const taskData = taskMap.get(taskKey);
      const userKey = user.id;
      
      // 新增：如果后续日志携带了更早的开始或更晚的结束，进行收敛（稳妥处理）
      if (task.startDate) {
        if (!taskData.startDate || dayjs(task.startDate).isBefore(dayjs(taskData.startDate))) {
          taskData.startDate = task.startDate;
        }
      }
      if (task.dueDate) {
        if (!taskData.dueDate || dayjs(task.dueDate).isAfter(dayjs(taskData.dueDate))) {
          taskData.dueDate = task.dueDate;
        }
      }
      
      if (!taskData.users.has(userKey)) {
        taskData.users.set(userKey, {
          userId: user.id,
          userName: user.realName,
          dailyHours: new Map(), // 用户层每日工时
          dailyLogs: new Map(),  // 用户层每日日志列表
        });
      }
      
      const userData = taskData.users.get(userKey);
      const dateKey = logDate.format('YYYY-MM-DD');
      
      // 累加用户层工时
      const prevUserHours = userData.dailyHours.get(dateKey) || 0;
      userData.dailyHours.set(dateKey, prevUserHours + hours);
      // 追加用户层日志
      const userLogs = userData.dailyLogs.get(dateKey) || [];
      userLogs.push(log);
      userData.dailyLogs.set(dateKey, userLogs);

      // 累加任务层工时
      const prevTaskHours = taskData.taskDailyHours.get(dateKey) || 0;
      taskData.taskDailyHours.set(dateKey, prevTaskHours + hours);
      // 追加任务层日志
      const taskLogs = taskData.taskDailyLogs.get(dateKey) || [];
      taskLogs.push(log);
      taskData.taskDailyLogs.set(dateKey, taskLogs);
    });
    
    return Array.from(taskMap.values()).map(task => ({
      ...task,
      users: Array.from(task.users.values())
    }));
  }, [logs]);

  // 获取工时颜色强度
  const getHoursIntensity = (hours) => {
    if (hours === 0) return 'transparent';
    if (hours <= 2) return '#e6f7ff';
    if (hours <= 4) return '#bae7ff';
    if (hours <= 6) return '#91d5ff';
    if (hours <= 8) return '#69c0ff';
    // 超过8小时使用红色高亮
    return '#ff4d4f';
  };

  // 新增：横向滚动容器的引用（必须在任何 return 之前声明，以满足 Hooks 规则）
  const containerRef = useRef(null);

  // 新增：任务折叠状态（存储被折叠的 taskId 集合）
  const [collapsedTaskIds, setCollapsedTaskIds] = useState(() => new Set());

  // 新增：单元格宽度（用于计划连续条定位），默认 48px
  const [dateCellWidth, setDateCellWidth] = useState(48);

  // 新增：调试日志，检查计划数据
  useEffect(() => {
    if (ganttData.length > 0) {
      console.log('[甘特图] 当前任务数据（含计划时间）：', ganttData.map(task => ({
        taskId: task.taskId,
        taskTitle: task.taskTitle, 
        startDate: task.startDate,
        dueDate: task.dueDate
      })));
    }
  }, [ganttData]);

  /**
   * 中文注释：测量日期单元格实际宽度
   * 说明：CSS 在小屏下将单元格宽度从 48px 改为 44px，原来写死的常量会导致计划条(left/width)计算偏移。
   * 做法：在头部区域获取第一个日期单元格的 clientWidth，存入状态，窗口大小变化时重新测量。
   */
  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      if (!container) return;
      const headerFirstCell = container.querySelector('.gantt-header .gantt-dates .gantt-date-cell');
      if (headerFirstCell) {
        const w = Math.round(headerFirstCell.getBoundingClientRect().width);
        if (w && w > 0 && w !== dateCellWidth) {
          setDateCellWidth(w);
        }
      }
    };

    // 初始测量
    measure();

    // 监听窗口尺寸变化，做节流简单处理
    let rAF = null;
    const onResize = () => {
      if (rAF) cancelAnimationFrame(rAF);
      rAF = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (rAF) cancelAnimationFrame(rAF);
    };
  }, [dateColumns, containerRef, dateCellWidth]);

  /**
   * 切换任务下用户行的展开/收起
   * @param {string|number} taskId 任务ID
   */
  const toggleTaskCollapsed = (taskId) => {
    setCollapsedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // 新增：进入页面或日期列变化时，自动将横向滚动条定位到“今天”，若不在范围则回到最左侧
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const raf = requestAnimationFrame(() => {
      const headerDates = container.querySelector('.gantt-header .gantt-dates');
      if (!headerDates) {
        container.scrollLeft = 0;
        return;
      }

      const cells = headerDates.querySelectorAll('.gantt-date-cell');
      if (!cells || cells.length === 0) {
        container.scrollLeft = 0;
        return;
      }

      // 查找“今天”所在的列元素（使用样式类名）
      let targetCell = null;
      for (let i = 0; i < cells.length; i += 1) {
        const el = cells[i];
        if (el.classList.contains('gantt-today')) {
          targetCell = el;
          break;
        }
      }

      if (targetCell) {
        // 将“今天”列滚动到可视区域左侧（紧邻固定列）
        const targetLeft = targetCell.offsetLeft;
        container.scrollTo({ left: Math.max(targetLeft - 4, 0), behavior: 'auto' });
      } else {
        // 不在范围内则回到最左侧
        container.scrollLeft = 0;
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [dateColumns]);

  // 工具函数：渲染 Tooltip 内容（展示全部日志）
  // showUser: 在任务汇总行中需要展示填报人；在用户行中可以省略
  const renderTooltipContent = (list = [], { showUser = true, titlePrefix = '' } = {}) => {
    if (!list || list.length === 0) return titlePrefix || '无日志';
    return (
      <div style={{ maxWidth: 480, maxHeight: 360, overflowY: 'auto' }}>
        {titlePrefix && (
          <div style={{ marginBottom: 6, color: '#d9d9d9' }}>{titlePrefix}</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((l, idx) => (
            <div
              key={idx}
              style={{
                padding: '8px 10px',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                color: '#262626', // 强制深色文本，避免继承 Tooltip 的浅色
                lineHeight: 1.6,
                wordBreak: 'break-word',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#595959' }}>
                <span>
                  {showUser ? (l?.user?.realName || l?.user?.name || '未知用户') + ' · ' : ''}
                  {dayjs(l.logDate).format('MM-DD')} · {Number(l.workHours || 0)}h
                </span>
                <span>进度 {Number(l.progressBefore ?? 0)}% → {Number(l.progressAfter ?? 0)}%</span>
              </div>
              {l.todayWork && (
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                  <strong>完成:</strong> {l.todayWork}
                </div>
              )}
              {l.tomorrowPlan && (
                <div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>
                  <strong>计划:</strong> {l.tomorrowPlan}
                </div>
              )}
              {l.issues && (
                <div style={{ marginTop: 2, whiteSpace: 'pre-wrap', color: '#ad4e00' }}>
                  <strong>问题:</strong> {l.issues}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 移动后的无数据早退：Hooks 之后再返回，遵守规则
  if (!logs.length || !dateRange.length) {
    return <Empty description="暂无数据" />;
  }

  return (
    <div className="gantt-chart">
      <div className="gantt-container" ref={containerRef}>
        <div className="gantt-header">
          <div className="gantt-task-column">任务/执行人</div>
          <div className="gantt-dates">
            {dateColumns.map(date => {
              const isToday = date.isSame(dayjs(), 'day');
              return (
                <div key={date.format('YYYY-MM-DD')} className={`gantt-date-cell ${isToday ? 'gantt-today' : ''}`}>
                  <div className="gantt-date-text">
                    {date.format('MM/DD')}
                  </div>
                  <div className="gantt-weekday">
                    {date.format('ddd')}
                  </div>
                  {isToday && <div className="gantt-today-indicator">今天</div>}
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="gantt-body">
          {ganttData.map(task => (
            <div key={task.taskId} className="gantt-task-group">
              {/* 任务标题行 */}
              <div className="gantt-row gantt-task-row">
                <div className="gantt-task-column">
                  <Text
                    strong
                    className="gantt-task-title"
                    onClick={() => toggleTaskCollapsed(task.taskId)}
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <span style={{ display: 'inline-block', width: 12, textAlign: 'center' }}>
                      {collapsedTaskIds.has(task.taskId) ? '▶' : '▼'}
                    </span>
                    {task.taskTitle}
                  </Text>
                </div>
                <div className="gantt-dates">
                  {(() => {
                    // 在任务行绘制“连续计划条”：优先使用任务 startDate/dueDate；
                    // 若缺失，则退化为“本日期范围内首次与最后一次出现日志/工时的日期区间”。
                    const start = task.startDate ? dayjs(task.startDate) : null;
                    const end = task.dueDate ? dayjs(task.dueDate) : null;
                    
                    let startIdx = null;
                    let endIdx = null;
                    
                    if (start || end) {
                      // 计算起止索引（严格同日匹配），并做边界裁剪
                      startIdx = 0;
                      endIdx = dateColumns.length - 1;
                      
                      if (start) {
                        const idx = dateColumns.findIndex(d => d.format('YYYY-MM-DD') === start.format('YYYY-MM-DD'));
                        startIdx = idx === -1 ? 0 : idx;
                      }
                      if (end) {
                        let lastIdx = -1;
                        for (let i = 0; i < dateColumns.length; i += 1) {
                          if (dateColumns[i].format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
                            lastIdx = i;
                          }
                        }
                        endIdx = lastIdx === -1 ? (dateColumns.length - 1) : lastIdx;
                      }
                    } else {
                      // 兜底：没有计划日期，使用“出现工时/日志的首末日”
                      const indicesWithWork = [];
                      for (let i = 0; i < dateColumns.length; i += 1) {
                        const dateKey = dateColumns[i].format('YYYY-MM-DD');
                        const totalHours = task.taskDailyHours?.get(dateKey) || 0;
                        const logsList = task.taskDailyLogs?.get(dateKey) || [];
                        if (totalHours > 0 || (logsList && logsList.length > 0)) {
                          indicesWithWork.push(i);
                        }
                      }
                      if (indicesWithWork.length > 0) {
                        startIdx = indicesWithWork[0];
                        endIdx = indicesWithWork[indicesWithWork.length - 1];
                      }
                    }
                    
                    if (startIdx === null || endIdx === null || startIdx > endIdx) return null;
                    
                    // 中文注释：使用索引 * 单元格宽度，保证与主体行坐标系一致，不再使用头部DOM几何计算
                    const left = startIdx * dateCellWidth;
                    const width = Math.max((endIdx - startIdx + 1) * dateCellWidth, 1);
                    return <div className="gantt-plan-range" style={{ left, width }} />;
                  })()}

                  {dateColumns.map(date => {
                    const dateKey = date.format('YYYY-MM-DD');
                    const isToday = date.isSame(dayjs(), 'day');
                    // 任务层：该任务在该日期的总工时与日志列表
                    const totalHours = task.taskDailyHours?.get(dateKey) || 0;
                    const logsList = task.taskDailyLogs?.get(dateKey) || [];
                    
                    // 中文注释：将8小时视为满格；超过8小时则满格并通过颜色提示超时
                    const percentWidth = Math.min(100, (totalHours / 8) * 100);
                    
                    return (
                      <div key={dateKey} className={`gantt-date-cell gantt-task-cell ${isToday ? 'gantt-today' : ''}`}>
                        {totalHours > 0 && (
                          <Tooltip title={renderTooltipContent(logsList, { showUser: true, titlePrefix: `总工时: ${totalHours.toFixed(1)}h` })}>
                            <div 
                              className="gantt-hours-bar gantt-task-bar"
                              style={{ 
                                backgroundColor: getHoursIntensity(totalHours), 
                                width: percentWidth >= 100 ? '100%' : `${percentWidth}%`
                              }}
                            >
                              {totalHours.toFixed(1)}
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {!collapsedTaskIds.has(task.taskId) && task.users.map(user => (
                <div key={user.userId} className="gantt-row gantt-user-row">
                  <div className="gantt-task-column">
                    <Text className="gantt-user-name">└ {user.userName}</Text>
                  </div>
                  <div className="gantt-dates">
                    {dateColumns.map(date => {
                      const dateKey = date.format('YYYY-MM-DD');
                      const isToday = date.isSame(dayjs(), 'day');
                      const hours = user.dailyHours.get(dateKey) || 0;
                      const logsList = user.dailyLogs?.get(dateKey) || [];
                      
                      // 中文注释：将8小时视为满格；超过8小时则满格并通过颜色提示超时
                      const percentWidth = Math.min(100, (hours / 8) * 100);
                      
                      return (
                        <div key={dateKey} className={`gantt-date-cell gantt-user-cell ${isToday ? 'gantt-today' : ''}`}>
                          {hours > 0 && (
                            <Tooltip title={renderTooltipContent(logsList, { showUser: false, titlePrefix: `${user.userName}: ${hours.toFixed(1)}h` })}>
                              <div 
                                className="gantt-hours-bar gantt-user-bar"
                                style={{ 
                                  backgroundColor: getHoursIntensity(hours), 
                                  width: percentWidth >= 100 ? '100%' : `${percentWidth}%`
                                }}
                              >
                                {hours.toFixed(1)}
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
          </div>
        </div>
      </div>
  );
};

// 中文函数注释：将任意日期/时间输入归一化为“本地日粒度”的 dayjs 对象（只保留 YYYY-MM-DD），避免因为时区或时间部分导致的跨日偏移
function normalizeToLocalDate(input) {
  if (!input) return null;
  // 若为字符串，优先截取前10位（YYYY-MM-DD），杜绝时区/时间造成的跨日
  if (typeof input === 'string') {
    const dateOnly = input.slice(0, 10); // 例如 '2024-09-23'
    const d1 = dayjs(dateOnly);
    if (d1.isValid()) return d1;
  }
  const d = dayjs(input);
  if (!d.isValid()) return null;
  // 兜底：通过格式化为 YYYY-MM-DD 再二次解析
  return dayjs(d.format('YYYY-MM-DD'));
}
export default GanttChart;